// Payoff computation engine for all trade types
// Returns: { metrics, curve, legs, breakevens, spot, zones }

// Generate a plain-language executive summary for each trade type
// Written for CFOs, board members, and sophisticated investors — not traders
// Parse number from user input — handles commas, $, %, spaces
function parseNum(v) {
  if (typeof v === "number") return v || 0;
  if (!v) return 0;
  const cleaned = String(v).replace(/[$,%\s]/g, "").replace(/,/g, "");
  return Number(cleaned) || 0;
}

export function generateExecutiveSummary(tradeId, fields) {
  const asset = fields.asset || "the underlying asset";
  const fmtN = (v) => {
    const num = parseNum(v);
    if (!num && num !== 0) return v;
    if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
    if (Math.abs(num) < 1 && num !== 0) return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    if (Math.abs(num) < 100) return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  // Format any value as $X,XXX.XX with commas
  const $ = (v) => {
    const num = parseNum(v);
    if (!num && num !== 0) return `$${v}`;
    if (Math.abs(num) < 1 && num !== 0) return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    if (Math.abs(num) < 100) return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  switch (tradeId) {
    case "long_seagull": {
      const spot = parseNum(fields.spot);
      const lp = parseNum(fields.lower_put), lc = parseNum(fields.lower_call), uc = parseNum(fields.upper_call);
      return `This is a zero-cost options structure on ${asset}, consisting of a sold put at ${fmtN(lp)}, a bought call at ${fmtN(lc)}, and a sold call at ${fmtN(uc)}. The premiums received from the two short legs fully offset the cost of the long call — net premium paid is zero.

Above ${fmtN(lc)} at expiry, the position generates profit up to a maximum of ${fmtN(fields.max_pnl)} at ${fmtN(uc)}. Above ${fmtN(uc)}, the sold call caps further participation. Below ${fmtN(lp)}, you are obligated to purchase ${asset} at that level — the maximum loss is not bounded by premium but by the put strike. The structure covers ${fields.contracts || "the specified number of"} contracts and expires ${fields.expiry || "on the target date"}.

The key trade-off: no upfront cost in exchange for capped upside at ${fmtN(uc)} and downside obligation below ${fmtN(lp)}. This structure is only appropriate if you are prepared to take delivery of ${asset} at the put strike.`;
    }

    case "reverse_cash_carry": {
      const pv = parseNum(fields.portfolio_value) || (parseNum(fields.spot_price) * parseNum(fields.btc_amount)) || 0;
      const crPct = parseNum(fields.cash_released_pct) || 85;
      const cashAmt = pv * (crPct / 100);
      const mgPct = parseNum(fields.margin_pct) || 15;
      const liqPx = parseNum(fields.spot_price) && parseNum(fields.btc_amount) ? parseNum(fields.spot_price) + (pv * mgPct / 100) / parseNum(fields.btc_amount) : 0;
      return `This structure holds spot ${asset} long while shorting an equivalent notional in perpetual futures on ${fields.exchange || "the designated exchange"}. The two legs offset each other's directional exposure, producing a delta-neutral book. The net effect: approximately ${fmtN(cashAmt)} (${crPct}% of position value) is freed from collateral requirements, with ${mgPct}% of portfolio value retained as margin on the short leg.

The ongoing cost of carry is the funding rate, currently estimated at ${fields.funding_rate || "~10"}% APR — paid when funding is positive (longs pay shorts). The short futures leg introduces liquidation risk if ${asset} rises sharply: ${liqPx ? `liquidation on the short begins near ${fmtN(liqPx)}, at which point margin is exhausted.` : "refer to exchange margin tables for the specific liquidation level."} ${fields.client_use_case ? ` Released capital is designated for: ${fields.client_use_case}.` : ""}

The structure does not change your net ${asset} exposure — a spot price increase still benefits the long leg in proportion to the short leg's loss. The primary risk is funding rate turning negative (shorts pay longs) or a sharp enough rally to trigger liquidation before the margin can be replenished.`;
    }

    case "covered_call": {
      const holdings = parseNum(fields.holdings) || 10;
      const premium = parseNum(fields.premium);
      const price = parseNum(fields.current_price);
      const costBasis = parseNum(fields.cost_basis);
      const strike = parseNum(fields.strike);
      const dte = parseNum(fields.dte);
      const notional = holdings * price;
      const annReturn = notional > 0 && dte > 0 ? ((premium / notional) * (365 / dte) * 100).toFixed(1) : "N/A";
      const premiumPerUnit = holdings > 0 ? (premium / holdings) : 0;
      return `You hold ${fields.holdings || 10} ${asset} with a current market price of ${$(fields.current_price)}. This trade sells a call at the ${$(fields.strike)} strike expiring ${fields.expiry || "on the target date"}, collecting ${premiumPerUnit > 0 && premiumPerUnit < 100 ? `${$(premiumPerUnit.toFixed(4))} per unit — ` : ""}${fmtN(premium)} total — or approximately ${annReturn}% annualized on the current price. The premium is received immediately and is yours regardless of outcome.

If ${asset} is below ${$(fields.strike)} at expiry, the option expires worthless and you retain your full position. If ${asset} is above ${$(fields.strike)} at expiry, your holdings are called away at that price. In essence, upside above ${$(fields.strike)} is forfeited.`;
    }

    case "cash_secured_put": {
      const effectiveBasis = parseNum(fields.effective_basis) || (parseNum(fields.strike) - parseNum(fields.premium));
      const discount = ((1 - effectiveBasis / parseNum(fields.current_price)) * 100).toFixed(1);
      return `${asset} is currently at ${$(fields.current_price)}. This trade sells a put at the ${$(fields.strike)} strike expiring ${fields.expiry || "on the target date"}, collecting ${$(fields.premium)} per unit. The full capital requirement of ${fmtN(fields.capital_required)} is held in reserve to cover potential assignment.

If ${asset} is above ${$(fields.strike)} at expiry, the option expires worthless and you keep the premium — no ${asset} is purchased. If ${asset} is at or below ${$(fields.strike)} at expiry, you are assigned and purchase ${asset} at an effective cost basis of ${$(effectiveBasis.toFixed(2))} — ${discount}% below the current spot price, net of premium received. There is no further downside protection below the strike; the position behaves identically to long spot below that level.

IV Rank is ${fields.iv_rank}%. The ${fields.delta} delta indicates the market-implied probability of expiring in the money is approximately ${(Math.abs(parseNum(fields.delta)) * 100).toFixed(0)}%. This trade is only appropriate if you are prepared to own ${asset} at the strike price.`;
    }

    case "leap": {
      const breakeven = (parseNum(fields.strike) + parseNum(fields.premium)).toFixed(2);
      const bePct = ((parseNum(fields.strike) + parseNum(fields.premium)) / parseNum(fields.current_price) * 100 - 100).toFixed(1);
      return `This is a long call position: ${fields.contracts || 1} contract(s) at the ${$(fields.strike)} strike expiring ${fields.expiry || "on the target date"}, purchased for ${fmtN(fields.total_outlay)} in total premium. ${asset} is currently at ${$(fields.current_price)}. The position has a delta of ${fields.delta} and ${fields.dte || "300+"} days to expiry.

The breakeven at expiry is ${$(breakeven)} — a ${bePct}% move from the current price. Above that level, the position is profitable in proportion to how far ${asset} exceeds the breakeven. The maximum loss is the ${fmtN(fields.total_outlay)} premium paid, incurred in full if ${asset} is at or below ${$(fields.strike)} at expiry.${fields.target_price ? ` At the stated target of ${$(fields.target_price)}, the intrinsic value at expiry would be ${$(Math.max(0, parseNum(fields.target_price) - parseNum(fields.strike)))} per unit.` : ""}

Time decay (theta) works against this position daily. A ${fields.dte || "300+"}-day expiry reduces — but does not eliminate — the rate of time value erosion versus shorter-dated options. If ${asset} does not reach the breakeven before expiry, the entire premium is lost.`;
    }

    case "wheel": {
      const adjBasis = parseNum(fields.cost_basis);
      const origStrike = parseNum(fields.original_strike);
      const curPrem = parseNum(fields.current_premium);
      const breakeven = adjBasis - curPrem;
      return `This is a summary of the ongoing Wheel strategy on ${asset}. After ${fields.cycles_completed || 0} completed cycles, cumulative premium collected totals ${$(fields.total_premium)}. Your adjusted cost basis is ${$(adjBasis)} per unit (down from the original ${$(origStrike)} put strike). The strategy is currently in the "${fields.current_phase || "active"}" phase.

The active leg is a ${$(fields.current_strike)} strike call generating ${$(fields.current_premium)} in premium this cycle. Including this cycle's premium, your breakeven sits at ${$(breakeven.toFixed(2))}. Each cycle alternates between cash-secured puts (when not holding the asset) and covered calls (when holding), with strike selection determining both the premium collected and the assignment risk.`;
    }

    case "collar": {
      const netCost = parseNum(fields.put_premium) - parseNum(fields.call_premium);
      const isCredit = netCost <= 0;
      return `You hold ${fields.holdings || 50} ${asset} at a cost basis of ${$(fields.cost_basis)}, currently priced at ${$(fields.current_price)}. This trade buys a put at ${$(fields.put_strike)} and sells a call at ${$(fields.call_strike)}, both expiring ${fields.expiry || "on the target date"}. Net cost: ${isCredit ? `credit of ${$(Math.abs(netCost).toFixed(2))} per unit received` : `${$(netCost.toFixed(2))} per unit paid`}.

Below ${$(fields.put_strike)}, losses on the spot position are offset by the put — your total downside is capped regardless of how far ${asset} falls. Above ${$(fields.call_strike)}, gains on the spot position are offset by the short call — you do not participate in any rally beyond that level. Between the two strikes, the position moves dollar-for-dollar with ${asset}. Total protected value: ${fmtN(fields.protected_value)}.

The collar converts an open-ended risk profile into a bounded range. The cost of that downside protection is the forfeiture of upside above ${$(fields.call_strike)}. If ${asset} rallies significantly above the call strike, the opportunity cost may be substantial relative to holding unhedged.`;
    }

    case "earnings_play": {
      const expectedMoveDollar = (parseNum(fields.current_price) * parseNum(fields.expected_move_pct) / 100).toFixed(0);
      const cushionPct = ((parseNum(fields.premium_collected) / parseNum(fields.current_price)) * 100).toFixed(1);
      return `${asset} is at ${$(fields.current_price)} ahead of the ${fields.event_date || "upcoming"} event. The options market is pricing a ±${fields.expected_move_pct}% implied move, equivalent to approximately ${$(expectedMoveDollar)} in either direction. The current ${fields.position_type || "options"} position is at the ${$(fields.strike)} strike, with ${$(fields.premium_collected)} in collected premium — a ${cushionPct}% buffer before the position begins losing.

For the position to remain profitable, ${asset} must stay within the implied move range on the relevant side. The last three comparable events produced: ${fields.last_3_reactions || "data not available"}. That history does not predict this outcome but provides context on whether the implied move is consistent with realized moves.

Assessment: <strong>${fields.recommendation || "under review"}</strong>. ${fields.recommendation === "Hold Through Event" ? `Premium collected exceeds the breakeven move required — the position has positive expected value relative to historical realized moves at this event type. Hold unless the position size is disproportionate to risk tolerance.` : fields.recommendation === "Close Before Event" ? `Closing before the event locks in accumulated premium and removes binary gap risk. The remaining time value does not justify the event exposure.` : fields.recommendation === "Roll to Later Date" ? `Rolling to a later expiry captures the remaining premium advantage while removing this specific event from the risk window.` : `Review position sizing ahead of the event. Binary events can produce moves that exceed the implied range.`}`;
    }

    case "call_spread": {
      const spot = parseNum(fields.spot);
      const longK = parseNum(fields.long_strike);
      const shortK = parseNum(fields.short_strike);
      const premium = parseNum(fields.premium);
      const dir = fields.direction || "Long";
      const isLong = dir === "Long";
      const absP = Math.abs(premium);
      const breakeven = isLong ? longK + absP : shortK + absP;
      const maxGain = isLong ? Math.max(shortK - longK + premium, 0) : absP;
      const maxLoss = isLong ? absP : Math.max(longK - shortK - absP, 0);
      return isLong
        ? `This is a bull call spread on ${fields.asset || "the underlying"}. You buy the ${fmtN(longK)} call and sell the ${fmtN(shortK)} call, paying a net debit of ${fmtN(absP)}, expiring ${fields.expiry || "on the target date"}.

The position profits above ${fmtN(breakeven)} at expiry, with maximum gain of ${fmtN(maxGain)} if the asset settles above ${fmtN(shortK)}. Maximum loss is the ${fmtN(absP)} debit if the asset expires below ${fmtN(longK)}. The spread provides leveraged upside exposure at a lower cost than a naked long call, in exchange for capping profit at the width of the spread.`
        : `This is a bear call spread on ${fields.asset || "the underlying"}. You sell the ${fmtN(shortK)} call and buy the ${fmtN(longK)} call as a hedge, collecting a net credit of ${fmtN(absP)}, expiring ${fields.expiry || "on the target date"}.

The full ${fmtN(absP)} credit is retained if the asset remains below ${fmtN(shortK)} at expiry. Losses begin above ${fmtN(breakeven)} and are capped at ${fmtN(maxLoss)} if the asset expires above ${fmtN(longK)}. This is a neutral-to-bearish income trade with strictly defined maximum loss.`;
    }

    case "put_spread": {
      const spot = parseNum(fields.spot);
      const longK = parseNum(fields.long_strike);
      const shortK = parseNum(fields.short_strike);
      const premium = parseNum(fields.premium);
      const dir = fields.direction || "Long";
      const isLong = dir === "Long";
      const absP = Math.abs(premium);
      const breakeven = isLong ? longK - absP : shortK - absP;
      const maxGain = isLong ? Math.max(longK - shortK + premium, 0) : absP;
      const maxLoss = isLong ? absP : Math.max(longK - shortK - absP, 0);
      return isLong
        ? `This is a bear put spread on ${fields.asset || "the underlying"}. You buy the ${fmtN(longK)} put and sell the ${fmtN(shortK)} put, paying a net debit of ${fmtN(absP)}, expiring ${fields.expiry || "on the target date"}.

The position profits below ${fmtN(breakeven)} at expiry, with maximum gain of ${fmtN(maxGain)} if the asset settles below ${fmtN(shortK)}. Maximum loss is the ${fmtN(absP)} debit if the asset expires above ${fmtN(longK)}. This is a defined-risk directional downside trade.`
        : `This is a bull put spread on ${fields.asset || "the underlying"}. You sell the ${fmtN(shortK)} put and buy the ${fmtN(longK)} put for protection, collecting a net credit of ${fmtN(absP)}, expiring ${fields.expiry || "on the target date"}.

The full ${fmtN(absP)} credit is retained if the asset remains above ${fmtN(shortK)} at expiry. Losses begin below ${fmtN(breakeven)} and are capped at ${fmtN(maxLoss)} if the asset expires below ${fmtN(longK)}. This is a neutral-to-bullish income trade with bounded maximum loss.`;
    }

    case "straddle": {
      const spot = parseNum(fields.spot);
      const atmK = parseNum(fields.atm_strike);
      const premium = parseNum(fields.total_premium);
      const dir = fields.direction || "Long";
      const isLong = dir === "Long";
      const absP = Math.abs(premium);
      const beLow = atmK - absP;
      const beHigh = atmK + absP;
      return isLong
        ? `This is a long straddle on ${fields.asset || "the underlying"} at the ${fmtN(atmK)} strike, expiring ${fields.expiry || "on the target date"}. You buy both the call and put at the same strike, paying a total debit of ${fmtN(absP)}.

The position profits if the asset moves sharply in either direction. The two breakevens are ${fmtN(beLow)} to the downside and ${fmtN(beHigh)} to the upside. Maximum loss is the ${fmtN(absP)} premium paid, occurring if the asset expires exactly at the strike. Implied volatility at ${fields.iv || "current levels"} needs to be realised — or exceeded — for the trade to be profitable.`
        : `This is a short straddle on ${fields.asset || "the underlying"} at the ${fmtN(atmK)} strike, expiring ${fields.expiry || "on the target date"}. You sell both the call and put at the same strike, collecting a total credit of ${fmtN(absP)}.

Maximum profit of ${fmtN(absP)} is achieved if the asset expires exactly at ${fmtN(atmK)}. The position begins losing outside the ${fmtN(beLow)}–${fmtN(beHigh)} range, with theoretically unlimited loss on a large move in either direction. This trade is a bet on implied volatility (${fields.iv || "current IV"}) exceeding realised volatility — a volatility crush or a range-bound market.`;
    }

    case "strangle": {
      const spot = parseNum(fields.spot);
      const callK = parseNum(fields.call_strike);
      const putK = parseNum(fields.put_strike);
      const premium = parseNum(fields.total_premium);
      const dir = fields.direction || "Long";
      const isLong = dir === "Long";
      const absP = Math.abs(premium);
      const beLow = putK - absP;
      const beHigh = callK + absP;
      return isLong
        ? `This is a long strangle on ${fields.asset || "the underlying"}, buying the ${fmtN(callK)} call and the ${fmtN(putK)} put, expiring ${fields.expiry || "on the target date"}. Total debit paid is ${fmtN(absP)}.

The position profits outside the ${fmtN(beLow)}–${fmtN(beHigh)} range. Between the two strikes, the full premium is lost. Compared to a straddle, the strangle costs less but requires a larger price move to reach breakeven. Maximum loss is the ${fmtN(absP)} premium, occurring anywhere between ${fmtN(putK)} and ${fmtN(callK)} at expiry.`
        : `This is a short strangle on ${fields.asset || "the underlying"}, selling the ${fmtN(callK)} call and the ${fmtN(putK)} put, expiring ${fields.expiry || "on the target date"}. Total credit received is ${fmtN(absP)}.

Maximum profit is the ${fmtN(absP)} premium, kept as long as the asset expires between ${fmtN(putK)} and ${fmtN(callK)}. Losses begin outside the ${fmtN(beLow)}–${fmtN(beHigh)} range. This is a range-bound income trade with unlimited theoretical loss on a large directional move. It is exposed to volatility expansion (vega risk) and requires active management if the asset approaches either strike.`;
    }

    case "call_spread_collar": {
      const spot     = parseNum(fields.spot);
      const notional = parseNum(fields.notional) || 1;
      const kp       = parseNum(fields.put_strike);
      const kc1      = parseNum(fields.short_call);
      const kc2      = parseNum(fields.long_call);
      const putPrem  = parseNum(fields.put_premium);
      const scPrem   = parseNum(fields.short_call_premium);
      const lcPrem   = parseNum(fields.long_call_premium);
      const netPremPerUnit = scPrem - putPrem - lcPrem;
      const netPremTotal   = netPremPerUnit * notional;
      const isCredit = netPremPerUnit >= 0;
      // Zone-aware breakeven: check linear zone first, else use above-kc2 formula
      const beLinear = spot - netPremPerUnit;
      const breakeven = (beLinear >= kp && beLinear <= kc1)
        ? beLinear
        : kc2 + (spot - kc1) - netPremPerUnit;
      const maxLossTotal = Math.abs((kp - spot) * notional + netPremTotal);
      const beAboveSpot = breakeven > spot;
      return `This structure holds ${notional} ${asset} at ${$(spot)} and overlays a three-leg options hedge expiring ${fields.expiry || "on the target date"}. The long put at ${$(kp)} floors downside losses at ${$(maxLossTotal)}; the short call at ${$(kc1)} ${isCredit ? "generates net premium income" : "partially offsets the net debit cost"} while capping gains; and the long call at ${$(kc2)} restores full participation above the re-entry level. Net premium is ${isCredit ? `a ${$(Math.abs(netPremTotal))} credit` : `a ${$(Math.abs(netPremTotal))} debit`}, with breakeven at ${$(Math.round(breakeven))}.`;
    }

    case "custom_spread": {
      const spot = parseNum(fields.spot);
      // Reconstruct active legs from the form to describe what was actually built.
      const legs = [];
      let netPremium = 0;
      for (let i = 1; i <= 4; i++) {
        const typeFull = (fields[`leg${i}_type`] || "None").toString().trim();
        if (!typeFull || typeFull === "None") continue;
        const isLong = typeFull.startsWith("Long");
        const isCall = typeFull.endsWith("Call");
        const strike = parseNum(fields[`leg${i}_strike`]);
        if (!(strike > 0)) continue;
        const qty = Math.max(1, parseNum(fields[`leg${i}_qty`]) || 1);
        const prem = Math.abs(parseNum(fields[`leg${i}_premium`]));
        legs.push({ side: isLong ? "long" : "short", kind: isCall ? "call" : "put", strike, qty, prem });
        netPremium += (isLong ? -1 : 1) * prem * qty;
      }
      if (legs.length === 0) {
        return `This is a custom multi-leg structure on ${asset || "the underlying"} that currently has no active legs configured. Add at least one leg (long or short call or put) on the parameters page to generate a meaningful payoff.`;
      }
      const isCredit = netPremium >= 0;
      const legPhrases = legs.map(l =>
        `${l.side === "long" ? "buy" : "sell"} ${l.qty} × the ${$(l.strike)} ${l.kind}${l.prem > 0 ? ` at ${$(l.prem)}` : ""}`
      );
      const legSentence = legPhrases.length === 1
        ? legPhrases[0]
        : legPhrases.slice(0, -1).join(", ") + ", and " + legPhrases[legPhrases.length - 1];
      return `This is a custom ${legs.length}-leg structure on ${asset || "the underlying"} at ${$(spot)}, expiring ${fields.expiry || "on the target date"}. The position is built to ${legSentence}. Net ${isCredit ? `credit received is ${$(Math.abs(netPremium))}` : `debit paid is ${$(Math.abs(netPremium))}`}. Because this structure was assembled freely from individual legs rather than a pre-defined template, max profit and max loss should be read directly from the payoff diagram and the metrics panel above — both depend entirely on the strike and quantity combination chosen.`;
    }

    case "binary_option": {
      const spot      = parseNum(fields.spot);
      const strike    = parseNum(fields.strike);
      const type      = fields.type || "Call";
      const direction = fields.direction || "Long";
      const isCall    = type === "Call";
      const isLong    = direction === "Long";
      const quantity  = Math.max(1, parseNum(fields.quantity) || 1);
      const premiumPct = Math.max(0.01, Math.min(99.99, parseNum(fields.premium)));
      const costPerContract = premiumPct / 100;
      const totalCost  = costPerContract * quantity;
      const totalPayout = quantity; // $1 per contract
      const payoutMultiple = costPerContract > 0 ? (1 / costPerContract) : 0;
      const condition = isCall ? `above ${$(strike)}` : `below ${$(strike)}`;
      const oppCondition = isCall ? `at or below ${$(strike)}` : `at or above ${$(strike)}`;
      return isLong
        ? `This is a long binary ${type.toLowerCase()} on ${asset || "the underlying"}, expiring ${fields.expiry || "on the target date"}. The position pays a fixed ${$(totalPayout)} (${quantity.toLocaleString()} contracts × $1) if ${asset || "the underlying"} settles ${condition}, and zero if it does not. Premium of ${premiumPct.toFixed(2)}% — equivalent to ${$(totalCost)} paid upfront — represents the market-implied probability of the event. If correct, net profit is ${$(totalPayout - totalCost)} (payout multiple of ${payoutMultiple.toFixed(2)}× the premium); if wrong, maximum loss is the ${$(totalCost)} premium paid. This is an all-or-nothing structure with no continuous payoff between the two states.`
        : `This is a short binary ${type.toLowerCase()} on ${asset || "the underlying"}, expiring ${fields.expiry || "on the target date"}. The position collects ${$(totalCost)} upfront (${premiumPct.toFixed(2)}% per contract) and keeps the full premium if ${asset || "the underlying"} settles ${oppCondition}. If the event triggers (price settles ${condition}), the seller pays ${$(totalPayout)}, for a maximum loss of ${$(totalPayout - totalCost)}. This is an all-or-nothing income trade where the premium effectively prices the market-implied probability of the event.`;
    }

    default:
      return "Trade analysis summary is being prepared.";
  }
}

// Universal engine imports — 7 pure option strategies delegate here
import { analyzeStructuredProduct } from "./structuredProductEngine.js";
import {
  adaptCashSecuredPut, adaptLeap, adaptCallSpread, adaptPutSpread,
  adaptStraddle, adaptStrangle, adaptLongSeagull, adaptCustomSpread,
} from "./strategyAdapters.js";

export function computeTradeAnalysis(tradeId, fields) {
  switch (tradeId) {
    // ── Universal engine strategies ──
    case "long_seagull":    return analyzeStructuredProduct(adaptLongSeagull(fields));
    case "cash_secured_put": return analyzeStructuredProduct(adaptCashSecuredPut(fields));
    case "leap":            return analyzeStructuredProduct(adaptLeap(fields));
    case "call_spread":     return analyzeStructuredProduct(adaptCallSpread(fields));
    case "put_spread":      return analyzeStructuredProduct(adaptPutSpread(fields));
    case "straddle":        return analyzeStructuredProduct(adaptStraddle(fields));
    case "strangle":        return analyzeStructuredProduct(adaptStrangle(fields));
    case "call_spread_collar": return computeCallSpreadCollar(fields);
    // ── Custom strategies (not yet migrated) ──
    case "reverse_cash_carry": return computeReverseCashCarry(fields);
    case "covered_call":    return computeCoveredCall(fields);
    case "wheel":           return computeWheel(fields);
    case "collar":          return computeCollar(fields);
    case "earnings_play":   return computeEarningsPlay(fields);
    case "binary_option":   return computeBinaryOption(fields);
    case "custom_spread":   return analyzeStructuredProduct(adaptCustomSpread(fields));
    default: return null;
  }
}

function n(v) {
  if (typeof v === "number") return v || 0;
  if (!v) return 0;
  // Strip $, commas, spaces, % before parsing
  const cleaned = String(v).replace(/[$,%\s]/g, "").replace(/,/g, "");
  return Number(cleaned) || 0;
}
function fmt(v, decimals = 0) {
  if (Math.abs(v) >= 1e6) {
    const m = v / 1e6;
    const isWholeM = Math.abs(m - Math.round(m)) < 0.05;
    return `$${m.toFixed(isWholeM ? 0 : 1)}M`;
  }
  if (Math.abs(v) >= 1e3) {
    const k = v / 1e3;
    if (decimals > 0) return `$${k.toFixed(decimals)}K`;
    // Auto: keep 1 decimal for non-whole thousands so $2,700 and $3,200 don't both
    // collapse to "$3K". Whole thousands still render cleanly (e.g. $70K, not $70.0K).
    const isWholeK = Math.abs(k - Math.round(k)) < 0.05;
    return `$${k.toFixed(isWholeK ? 0 : 1)}K`;
  }
  if (Math.abs(v) < 1 && v !== 0) return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  if (Math.abs(v) < 100 && v !== 0) return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: decimals })}`;
}
// Full comma-formatted dollar amount (no abbreviation)
function fmtFull(v) {
  const num = typeof v === "number" ? v : parseFloat(String(v).replace(/[$,%\s,]/g, ""));
  if (isNaN(num)) return `$${v}`;
  if (Math.abs(num) < 1 && num !== 0) return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function buildCurve(minPrice, maxPrice, pnlFn, steps = 200) {
  const curve = [];
  for (let i = 0; i <= steps; i++) {
    const price = minPrice + (maxPrice - minPrice) * (i / steps);
    curve.push({ price, pnl: pnlFn(price) });
  }
  return curve;
}

// --- LONG SEAGULL ---
function computeLongSeagull(f) {
  const spot = n(f.spot);
  const contracts = n(f.contracts) || 1;
  const lp = n(f.lower_put);
  const lc = n(f.lower_call);
  const uc = n(f.upper_call);

  const minP = lp * 0.75;
  const maxP = uc * 1.25;

  const curve = buildCurve(minP, maxP, (price) => {
    let pnl = 0;
    if (price < lp) pnl += (price - lp);
    if (price > lc) pnl += (price - lc);
    if (price > uc) pnl -= (price - uc);
    return pnl * contracts;
  });

  const maxProfit = (uc - lc) * contracts;
  const maxLoss = lp * contracts;

  return {
    curve,
    spot,
    breakevens: [lc],
    metrics: [
      { label: "Spot Price", value: fmt(spot), sub: f.asset || "—" },
      { label: "Max Profit", value: fmt(maxProfit), sub: `Capped at ${fmt(uc)}`, positive: true },
      { label: "Max Loss", value: fmt(maxLoss), sub: `Below ${fmt(lp)}`, negative: true },
      { label: "Contracts", value: contracts.toLocaleString(), sub: "Units" },
      { label: "Expiry", value: f.expiry || "—", sub: "Target" },
    ],
    legs: [
      { action: "SELL", type: "Put", strike: lp, label: `${fmt(lp)} Put`, color: "#ef4444" },
      { action: "BUY", type: "Call", strike: lc, label: `${fmt(lc)} Call`, color: "#4ADE80" },
      { action: "SELL", type: "Call", strike: uc, label: `${fmt(uc)} Call`, color: "#ef4444" },
    ],
    zones: [
      { from: minP, to: lp, label: "Max Loss Zone", color: "rgba(239,68,68,0.08)" },
      { from: lc, to: uc, label: "Profit Zone", color: "rgba(74,222,128,0.08)" },
    ],
  };
}

// --- REVERSE CASH & CARRY ---
// Delta-neutral: long spot + short perp. Net P&L is ~flat (funding cost only).
// Chart shows combined P&L: spot gain/loss + perp gain/loss + funding cost.
// Liquidation risk if price rises sharply and margin on short perp is exhausted.
function computeReverseCashCarry(f) {
  const spotPrice = n(f.spot_price);
  const units = n(f.btc_amount);
  const portfolio = n(f.portfolio_value) || spotPrice * units;
  const marginPct = n(f.margin_pct) || 15;
  const cashPct = n(f.cash_released_pct) || 85;
  const fundingRate = n(f.funding_rate) || 10;

  const cashReleased = portfolio * (cashPct / 100);
  const marginPosted = portfolio * (marginPct / 100);
  const annualFundingCost = portfolio * (fundingRate / 100);
  const monthlyFundingCost = annualFundingCost / 12;

  // Liquidation: perp loss eats all margin
  const liqPrice = spotPrice > 0 ? spotPrice + marginPosted / units : 0;
  const warningPrice = spotPrice > 0 ? spotPrice + (marginPosted * 0.5) / units : 0;

  // Chart: combined P&L of long spot + short perp
  const minP = spotPrice * 0.7;
  const maxP = liqPrice * 1.15;

  const curve = buildCurve(minP, maxP, (price) => {
    // Long spot P&L
    const spotPnl = (price - spotPrice) * units;
    // Short perp P&L (opposite of spot)
    const perpPnl = (spotPrice - price) * units;
    // Combined = nearly zero (delta-neutral)
    let combined = spotPnl + perpPnl;

    // After liquidation, short perp is closed — only long spot remains
    if (price >= liqPrice) {
      // Margin is fully lost, perp is liquidated at liqPrice
      const perpLossAtLiq = (liqPrice - spotPrice) * units;
      combined = spotPnl - perpLossAtLiq;
    }

    // Subtract ongoing funding cost (annualized, shown as drag)
    combined -= monthlyFundingCost;

    return combined;
  });

  return {
    curve,
    spot: spotPrice,
    breakevens: [liqPrice],
    spotQuantity: 0,
    legPayoffs: [],
    metrics: [
      { label: "Portfolio Value", value: fmt(portfolio), sub: `${units.toLocaleString()} ${f.asset || "units"}` },
      { label: "Cash Released", value: fmt(cashReleased), sub: `${cashPct}% unlocked`, positive: true },
      { label: "Margin Posted", value: fmt(marginPosted), sub: `${marginPct}% locked` },
      { label: "Liquidation Price", value: fmt(liqPrice), sub: `+${((liqPrice / spotPrice - 1) * 100).toFixed(1)}% from spot`, negative: true },
      { label: "Funding Cost", value: fmt(monthlyFundingCost) + "/mo", sub: `${fundingRate}% APR` },
      { label: "Venue", value: f.exchange || "—", sub: "Execution" },
    ],
    legs: [
      { action: "HOLD", type: `Spot ${f.asset || "Asset"}`, strike: spotPrice, label: `${units.toLocaleString()} ${f.asset || "units"} @ ${fmt(spotPrice)}`, color: "#F7931A" },
      { action: "SHORT", type: "Perp Futures", strike: spotPrice, label: `Short ${units.toLocaleString()} ${f.asset || "units"} Perp`, color: "#ef4444" },
    ],
    zones: [
      { from: minP, to: warningPrice, label: "Safe Zone", color: "rgba(74,222,128,0.08)" },
      { from: warningPrice, to: liqPrice, label: "Warning Zone", color: "rgba(251,191,36,0.10)" },
      { from: liqPrice, to: maxP, label: "Liquidation", color: "rgba(239,68,68,0.12)" },
    ],
  };
}

// --- COVERED CALL ---
function computeCoveredCall(f) {
  const price = n(f.current_price);
  const strike = n(f.strike);
  const cost = n(f.cost_basis);
  const premium = Math.abs(n(f.premium)); // always received — force positive; TOTAL for whole position (NOT per unit)
  const holdings = n(f.holdings) || 10;
  const dte = n(f.dte);

  // Core calculations — full precision, no rounding until display
  const premiumPerUnit = holdings > 0 ? premium / holdings : 0;
  const currentNotional = holdings * price;
  const breakeven = cost - premiumPerUnit;
  const unrealizedGain = (price - cost) * holdings;
  const maxStrategyPnl = (strike - cost) * holdings + premium;
  const premiumYield = currentNotional > 0 ? premium / currentNotional : 0;
  const annualizedPremiumYield = dte > 0 ? premiumYield * (365 / dte) : 0;
  const annReturnDisplay = dte > 0 && currentNotional > 0 ? (annualizedPremiumYield * 100).toFixed(1) : "N/A";
  const otmPct = price > 0 ? (((strike - price) / price) * 100).toFixed(1) : "N/A";

  const minP = Math.max(price * 0.5, 0.01);
  const maxP = strike * 1.3;

  // Analytical P&L function — used by both curve and scenario table for exact values
  const pnlAtPrice = (p) => {
    if (p <= strike) return ((p - cost) * holdings) + premium;
    return ((strike - cost) * holdings) + premium;   // capped
  };

  const curve = buildCurve(minP, maxP, pnlAtPrice);

  // Use fmtExact for consistent $ formatting (no K/M abbreviation)
  const fmtExact = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtExact2 = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return {
    curve, spot: price, breakevens: [breakeven],
    tradeType: "covered_call",
    costBasis: cost,
    currentNotional,
    pnlAtPrice,
    spotQuantity: holdings,
    legPayoffs: [
      { label: "Long Spot", color: "#00C2FF", fn: (p) => (p - cost) * holdings },
      { label: "Short Call", color: "#ef4444", fn: (p) => -Math.max(0, p - strike) * holdings },
    ],
    metrics: [
      { label: "Current Price", value: fmtFull(price), sub: f.asset || "—" },
      { label: "Strike Price", value: fmtFull(strike), sub: `${otmPct}% OTM` },
      { label: "Expiry", value: f.expiry || "—", sub: dte > 0 ? `${dte} DTE` : "—" },
      { label: "Premium Income", value: fmtExact(premium), sub: `${annReturnDisplay}% ann. premium yield`, positive: true },
      { label: "Breakeven", value: fmtExact2(breakeven), sub: "Downside protection" },
      { label: "Unrealized Gain", value: fmtExact(unrealizedGain), sub: `${holdings} units · ${f.iv_rank || "—"}% IV Rank`, positive: unrealizedGain >= 0 },
    ],
    legs: [
      { action: "HOLD", type: `Spot ${f.asset || "Asset"}`, strike: cost, label: `${holdings} units @ ${fmtFull(cost)}`, color: "#00C2FF" },
      { action: "SELL", type: "Call", strike, label: `${fmtFull(strike)} Call · ${fmtExact(premium)} total`, color: "#ef4444" },
    ],
    zones: [
      { from: breakeven, to: strike, label: "Profit Zone", color: "rgba(74,222,128,0.08)" },
    ],
  };
}

// --- CASH-SECURED PUT ---
function computeCashSecuredPut(f) {
  const price = n(f.current_price);
  const strike = n(f.strike);
  const premium = Math.abs(n(f.premium)); // always received — force positive
  const dte = n(f.dte);
  const capital = n(f.capital_required);
  const effectiveBasis = n(f.effective_basis) || (strike - premium);

  const breakeven = strike - premium;
  const maxProfit = premium;
  const returnOnCapital = capital > 0 ? ((premium / capital) * (365 / (dte || 30)) * 100).toFixed(1) : "N/A";

  const minP = strike * 0.75;
  const maxP = price * 1.15;

  const curve = buildCurve(minP, maxP, (p) => {
    if (p >= strike) return premium;
    return p - strike + premium;
  });

  const discountToCurrent = price > 0 ? ((1 - effectiveBasis / price) * 100).toFixed(1) : "0";

  return {
    curve, spot: price, breakevens: [breakeven],
    metrics: [
      { label: "Current Price", value: fmtFull(price), sub: f.asset || "—" },
      { label: "Premium Income", value: fmtFull(maxProfit), sub: `${returnOnCapital}% ann. return`, positive: true },
      { label: "Breakeven", value: fmtFull(breakeven.toFixed(2)), sub: "If assigned" },
      { label: "Effective Basis", value: fmtFull(effectiveBasis), sub: `${discountToCurrent}% below spot`, positive: true },
      { label: "Capital Req.", value: fmt(capital), sub: `${f.iv_rank}% IV · ${dte}d · ${f.delta} Δ` },
    ],
    legs: [
      { action: "SELL", type: "Put", strike, label: `${fmtFull(strike)} Put @ ${fmtFull(premium)}`, color: "#A78BFA" },
    ],
    zones: [
      { from: breakeven, to: maxP, label: "Profit Zone", color: "rgba(74,222,128,0.08)" },
    ],
  };
}

// --- LONG-DATED OPTION ---
function computeLeap(f) {
  const price = n(f.current_price);
  const strike = n(f.strike);
  const premium = Math.abs(n(f.premium)); // always a cost — force positive
  const contracts = n(f.contracts) || 1;
  const totalOutlay = Math.abs(n(f.total_outlay) || premium * contracts); // always paid
  const dte = n(f.dte);
  const target = n(f.target_price);

  const breakeven = strike + premium;
  const chartMax = target > breakeven * 1.4 ? target * 1.1 : breakeven * 1.4;
  const minP = strike * 0.7;
  const maxP = chartMax;

  const curve = buildCurve(minP, maxP, (p) => {
    if (p <= strike) return -totalOutlay;
    return ((p - strike) * contracts) - totalOutlay;
  });

  // Key stats
  const profitAtTarget = target > strike ? ((target - strike) * contracts) - totalOutlay : null;
  const returnOnCapital = profitAtTarget !== null && totalOutlay > 0 ? (profitAtTarget / totalOutlay * 100) : null;
  const leverageMultiple = price > 0 && premium > 0 ? (n(f.delta) * price / premium) : null;

  const metrics = [
    { label: "Current Price", value: fmtFull(price), sub: f.asset || "—" },
    { label: "Capital at Risk", value: fmt(totalOutlay), sub: `${contracts} contracts`, negative: true },
    { label: "Expiry", value: f.expiry || "—", sub: dte > 0 ? `${dte} DTE` : "—" },
    { label: "Breakeven", value: fmtFull(breakeven.toFixed(2)), sub: `+${((breakeven / price - 1) * 100).toFixed(1)}%` },
  ];

  if (profitAtTarget !== null) {
    metrics.push({ label: "Profit at Target", value: fmt(profitAtTarget), sub: `at ${fmtFull(target)}`, positive: true });
    metrics.push({ label: "Return on Capital", value: `${returnOnCapital.toFixed(0)}%`, sub: `${(profitAtTarget / totalOutlay + 1).toFixed(1)}x capital`, positive: true });
  }

  metrics.push({ label: "Delta / Leverage", value: `${f.delta} / ${leverageMultiple ? leverageMultiple.toFixed(1) + "x" : "—"}`, sub: `${contracts} contracts` });

  return {
    curve, spot: price, breakevens: [breakeven],
    ...(target ? { annotations: [{ price: target, label: "Target", color: "#4ADE80" }] } : {}),
    metrics,
    legs: [
      { action: "BUY", type: "Call (Long-Dated)", strike, label: `${fmtFull(strike)} Call @ ${fmtFull(premium)}`, color: "#FB923C" },
    ],
    zones: [
      { from: breakeven, to: maxP, label: "Profit Zone", color: "rgba(74,222,128,0.08)" },
    ],
  };
}

// --- THE WHEEL ---
// Cycles between selling cash-secured puts and covered calls.
//
// ACCOUNTING MODEL (single source of truth):
//   cost_basis = "Current Adjusted Cost Basis" — already reflects historical premium.
//   totalPremium = informational only (displayed for reference, NOT used in P&L math).
//   currentPremium = premium from the CURRENT active cycle — the only forward-looking income.
//
// Therefore:
//   strategy_pnl = (expiry_price - costBasis) + currentPremium   (capped at strike if selling calls)
//   breakeven = costBasis - currentPremium
//   max_profit = (callStrike - costBasis) + currentPremium        (if called away)
//
// DO NOT add totalPremium to P&L — it is already embedded in costBasis.
function computeWheel(f) {
  const price = n(f.current_price);
  const holdings = n(f.holdings) || 1;
  const costBasis = n(f.cost_basis);           // Already adjusted for historical premium
  const totalPremium = n(f.total_premium);     // Informational only — NOT used in P&L
  const currentStrike = n(f.current_strike);
  const currentPremium = Math.abs(n(f.current_premium)); // always received — force positive; TOTAL for all units
  const cycles = n(f.cycles_completed);
  const dte = n(f.dte) || 30;

  const isSellingPuts = f.current_phase === "Selling Puts";
  const isSellingCalls = f.current_phase === "Selling Covered Calls";

  // Per-unit premium for breakeven calc
  const premiumPerUnit = holdings > 0 ? currentPremium / holdings : currentPremium;

  // Position-level notional
  const currentNotional = price * holdings;

  // Annualized return: currentPremium / currentNotional * (365 / DTE)
  const premiumYield = currentNotional > 0 ? currentPremium / currentNotional : 0;
  const annualizedReturn = dte > 0 && currentNotional > 0
    ? (premiumYield * (365 / dte) * 100).toFixed(1)
    : "N/A";

  // Per-unit breakeven
  const breakeven = isSellingPuts
    ? currentStrike - premiumPerUnit
    : costBasis - premiumPerUnit;

  let minP, maxP;
  if (isSellingPuts) {
    minP = breakeven * 0.8;
    maxP = currentStrike * 1.3;
  } else {
    minP = breakeven * 0.8;
    maxP = currentStrike * 1.2;
  }

  // Analytical P&L function — position-level (includes holdings)
  const pnlAtPrice = (p) => {
    if (isSellingPuts) {
      if (p >= currentStrike) return currentPremium;
      return ((p - currentStrike) * holdings) + currentPremium;
    }
    if (isSellingCalls && p > currentStrike) {
      return ((currentStrike - costBasis) * holdings) + currentPremium;  // capped
    }
    return ((p - costBasis) * holdings) + currentPremium;
  };

  const curve = buildCurve(minP, maxP, pnlAtPrice);

  const maxProfit = isSellingCalls
    ? ((currentStrike - costBasis) * holdings) + currentPremium
    : null;

  // Format helpers
  const fmtExact = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtExact2 = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return {
    curve, spot: price, breakevens: [breakeven],
    tradeType: "wheel",
    costBasis,
    currentNotional,
    pnlAtPrice,
    spotQuantity: isSellingCalls ? holdings : 0,
    legPayoffs: isSellingCalls ? [
      { label: "Long Spot", color: "#00C2FF", fn: (p) => (p - costBasis) * holdings },
      { label: "Short Call", color: "#34D399", fn: (p) => -Math.max(0, p - currentStrike) * holdings },
    ] : [
      { label: "Short Put", color: "#34D399", fn: (p) => -Math.max(0, currentStrike - p) * holdings },
    ],
    metrics: [
      { label: "Current Price", value: fmtFull(price), sub: `${f.asset || "—"} · ${holdings} unit${holdings !== 1 ? "s" : ""}` },
      { label: "Phase", value: f.current_phase || "—", sub: `Cycle ${cycles}` },
      { label: "Adj. Cost Basis", value: fmtExact(costBasis), sub: `Original: ${fmtExact(n(f.original_strike))}` },
      { label: "Expiry", value: f.expiry || "—", sub: `${dte} DTE` },
      { label: "Current Premium", value: fmtExact(currentPremium), sub: `Total collected: ${fmtExact(totalPremium)}`, positive: true },
      { label: "Breakeven", value: fmtExact2(breakeven), sub: `${price > 0 ? ((1 - breakeven / price) * 100).toFixed(1) : 0}% below spot` },
      { label: "Ann. Premium Yield", value: `${annualizedReturn}%`, sub: `Simple · ${dte}d cycle`, positive: true },
      ...(maxProfit ? [{ label: "Max Profit", value: fmtExact(maxProfit), sub: `Called away @ ${fmtExact(currentStrike)}`, positive: true }] : []),
    ],
    legs: isSellingPuts
      ? [{ action: "SELL", type: "Put", strike: currentStrike, label: `${fmtFull(currentStrike)} Put @ ${fmtExact(currentPremium)}`, color: "#34D399" }]
      : isSellingCalls
        ? [
            { action: "HOLD", type: `Spot ${f.asset || "Asset"}`, strike: costBasis, label: `Position @ ${fmtExact(costBasis)} adj. basis`, color: "#00C2FF" },
            { action: "SELL", type: "Call", strike: currentStrike, label: `${fmtFull(currentStrike)} Call @ ${fmtExact(currentPremium)}`, color: "#34D399" },
          ]
        : [{ action: "HOLD", type: `Spot ${f.asset || "Asset"}`, strike: costBasis, label: `Position @ ${fmtExact(costBasis)} adj. basis`, color: "#00C2FF" }],
    zones: [],
  };
}

// --- COLLAR ---
function computeCollar(f) {
  const price = n(f.current_price);
  const cost = n(f.cost_basis);
  const putStrike = n(f.put_strike);
  const callStrike = n(f.call_strike);
  const putPrem = Math.abs(n(f.put_premium));   // you pay this — force positive
  const callPrem = Math.abs(n(f.call_premium)); // you receive this — force positive
  const holdings = n(f.holdings) || 50;
  const netCost = putPrem - callPrem; // positive = net debit, negative = net credit
  const protectedVal = n(f.protected_value);

  const minP = putStrike * 0.85;
  const maxP = callStrike * 1.15;

  const pnlAtPrice = (p) => {
    let pnl = (p - cost - netCost) * holdings;
    if (p < putStrike) pnl = (putStrike - cost - netCost) * holdings;
    if (p > callStrike) pnl = (callStrike - cost - netCost) * holdings;
    return pnl;
  };

  const curve = buildCurve(minP, maxP, pnlAtPrice);

  const maxProfit = (callStrike - cost - netCost) * holdings;

  return {
    curve, spot: price, breakevens: [cost + netCost],
    tradeType: "collar",
    costBasis: cost,
    pnlAtPrice,
    spotQuantity: holdings,
    legPayoffs: [
      { label: "Long Spot", color: "#00C2FF", fn: (p) => (p - cost) * holdings },
      { label: "Long Put",  color: "#F472B6", fn: (p) => Math.max(0, putStrike - p) * holdings },
      { label: "Short Call", color: "#ef4444", fn: (p) => -Math.max(0, p - callStrike) * holdings },
    ],
    metrics: [
      { label: "Current Price", value: fmtFull(price), sub: f.asset || "—" },
      { label: "Protected Floor", value: fmtFull(putStrike), sub: `${holdings} units` },
      { label: "Upside Cap", value: fmtFull(callStrike), sub: `Capped upside`, positive: true },
      { label: "Expiry", value: f.expiry || "—", sub: `${holdings} units protected` },
      { label: "Net Cost", value: fmtFull(netCost.toFixed(2)), sub: netCost <= 0 ? "Credit" : "Debit", positive: netCost <= 0 },
      { label: "Value Protected", value: fmt(protectedVal) },
    ],
    legs: [
      { action: "HOLD", type: `Spot ${f.asset || "Asset"}`, strike: cost, label: `${holdings} units @ ${fmtFull(cost)}`, color: "#00C2FF" },
      { action: "BUY", type: "Put", strike: putStrike, label: `${fmtFull(putStrike)} Put @ ${fmtFull(putPrem)}`, color: "#F472B6" },
      { action: "SELL", type: "Call", strike: callStrike, label: `${fmtFull(callStrike)} Call @ ${fmtFull(callPrem)}`, color: "#ef4444" },
    ],
    zones: [
      { from: putStrike, to: callStrike, label: "Active Range", color: "rgba(244,114,182,0.06)" },
    ],
  };
}

// --- CALL SPREAD COLLAR ---
// Long spot + BUY put (floor) + SELL ATM call (soft cap) + BUY OTM call (tail re-participation)
// This is a HEDGED position — payoff includes the underlying spot move.
function computeCallSpreadCollar(f) {
  const spot        = n(f.spot);
  const notional    = n(f.notional) || 1;
  const kp          = n(f.put_strike);    // BUY put — floor
  const kc1         = n(f.short_call);   // SELL call — soft cap start
  const kc2         = n(f.long_call);    // BUY call — re-participation
  const putPrem     = Math.abs(n(f.put_premium));         // you pay this — force positive
  const scPrem      = Math.abs(n(f.short_call_premium));  // you receive this — force positive
  const lcPrem      = Math.abs(n(f.long_call_premium));   // you pay this — force positive

  // Net premium (total, signed): positive = net credit received
  const netPremTotal    = (scPrem - putPrem - lcPrem) * notional;
  const netPremPerUnit  = scPrem - putPrem - lcPrem;

  // Combined payoff: long spot + options overlay + net premium
  const hedgedPnl = (S) => {
    const spotPnl  = (S - spot) * notional;
    const putIntr  = Math.max(0, kp - S) * notional;
    const scIntr   = -Math.max(0, S - kc1) * notional;
    const lcIntr   = Math.max(0, S - kc2) * notional;
    return spotPnl + putIntr + scIntr + lcIntr + netPremTotal;
  };

  // Spot-only P&L (un-hedged — what you'd make/lose just holding spot)
  const spotPnlAtPrice = (S) => (S - spot) * notional;

  // Delta: how much the hedge adds vs raw spot (positive = protection, negative = cap cost)
  const deltaAtPrice = (S) => hedgedPnl(S) - spotPnlAtPrice(S);

  // Analytical breakeven — detect which zone it falls in:
  // Zone 1 [kp, kc1]: hedgedPnl = (S - spot)*N + netPremTotal = 0  → S = spot - netPremPerUnit
  // Zone 2 [kc2, ∞]:  hedgedPnl = S + kc1 - kc2 - spot + netPremTotal = 0
  //                              → S = kc2 + (spot - kc1) - netPremPerUnit
  const beLinear = spot - netPremPerUnit;
  const breakeven = (beLinear >= kp && beLinear <= kc1)
    ? beLinear
    : kc2 + (spot - kc1) - netPremPerUnit; // breakeven is above re-entry level

  // Floor P&L (maximum loss — flat for all S < kp)
  const maxLoss = (kp - spot) * notional + netPremTotal; // negative

  // Capped P&L (flat for kc1 ≤ S < kc2)
  const cappedProfit = (kc1 - spot) * notional + netPremTotal;

  const minP = kp * 0.7;
  const maxP = kc2 * 1.4;

  const curve = buildCurve(minP, maxP, hedgedPnl);

  return {
    curve,
    spot,
    breakevens: [breakeven],
    tradeType: "call_spread_collar",
    pnlAtPrice: hedgedPnl,
    spotPnlAtPrice,
    deltaAtPrice,
    netPremPerUnit,
    spotQuantity: notional,
    legPayoffs: [
      { label: "Long Spot",  color: "#00C2FF", fn: (p) => (p - spot) * notional },
      { label: "Long Put",   color: "#4ADE80", fn: (p) => Math.max(0, kp - p) * notional },
      { label: "Short Call", color: "#ef4444", fn: (p) => -Math.max(0, p - kc1) * notional },
      { label: "Long Call",  color: "#F97316", fn: (p) => Math.max(0, p - kc2) * notional },
    ],
    // Strike levels exposed for scenario table rendering
    kp, kc1, kc2, notional,
    metrics: [
      { label: "Spot Price",         value: fmtFull(spot),                                  sub: f.asset || "BTC" },
      { label: "Net Premium",        value: fmt(Math.abs(netPremTotal)),                     sub: netPremTotal >= 0 ? "Credit" : "Debit", positive: netPremTotal >= 0 },
      { label: "Breakeven",          value: fmtFull(Math.round(breakeven)),                 sub: breakeven > spot ? "Above spot entry" : "Below spot entry" },
      { label: "Downside Floor",     value: fmtFull(kp),                                    sub: "Protection activates below this level" },
      { label: "Max Protected Loss", value: fmt(Math.abs(maxLoss)),                          sub: `vs spot entry`, negative: true },
      { label: "Soft Cap Range",     value: `${fmtFull(kc1)} – ${fmtFull(kc2)}`,           sub: "Gains resume above re-entry level" },
      { label: "Re-participation",   value: fmtFull(kc2),                                   sub: "Tail upside restored above this" },
      { label: "Expiry",             value: f.expiry || "—" },
    ],
    legs: [
      { action: "HOLD", type: `Spot ${f.asset || "BTC"}`, strike: spot,  label: `${notional} unit${notional !== 1 ? "s" : ""} @ ${fmtFull(spot)}`,           color: "#00C2FF" },
      { action: "BUY",  type: "Put",                      strike: kp,    label: `${fmtFull(kp)} Put — Downside Floor @ ${fmtFull(putPrem)}`,                  color: "#4ADE80" },
      { action: "SELL", type: "Call",                     strike: kc1,   label: `${fmtFull(kc1)} Call — Soft Cap @ ${fmtFull(scPrem)}`,                       color: "#ef4444" },
      { action: "BUY",  type: "Call",                     strike: kc2,   label: `${fmtFull(kc2)} Call — Tail Re-entry @ ${fmtFull(lcPrem)}`,                  color: "#F97316" },
    ],
    zones: [
      { from: 0,   to: kp,      label: "Protected Zone",   color: "rgba(74,222,128,0.07)"  },
      { from: kc1, to: kc2,     label: "Soft Cap Zone",    color: "rgba(239,68,68,0.07)"   },
      { from: kc2, to: kc2 * 2, label: "Re-participation", color: "rgba(249,115,22,0.07)"  },
    ],
  };
}

// --- EVENT RISK ANALYSIS ---
function computeEarningsPlay(f) {
  const price = n(f.current_price);
  const move = n(f.expected_move_pct);
  const strike = n(f.strike);
  const premCollected = Math.abs(n(f.premium_collected)); // always received — force positive

  const downTarget = price * (1 - move / 100);
  const upTarget = price * (1 + move / 100);
  const minP = price * 0.75;
  const maxP = price * 1.25;

  const posType = f.position_type || "Short Put";
  const pnlAtPrice = (p) => {
    switch (posType) {
      case "Short Put":
        if (p >= strike) return premCollected;
        return p - strike + premCollected;
      case "Long Call":
        if (p <= strike) return -premCollected;
        return (p - strike) - premCollected;
      case "Covered Call":
        if (p > strike) return strike - price + premCollected;
        return p - price + premCollected;
      case "Short Call Spread":
        if (p <= strike) return premCollected;
        return premCollected - (p - strike);
      default:
        return 0;
    }
  };
  const curve = buildCurve(minP, maxP, pnlAtPrice);

  const cushion = price > 0 ? ((premCollected / price) * 100).toFixed(1) : "0";

  return {
    curve, spot: price, breakevens: [strike - premCollected],
    pnlAtPrice,
    spotQuantity: posType === "Covered Call" ? 1 : 0,
    legPayoffs: posType === "Covered Call" ? [
      { label: "Long Spot",  color: "#00C2FF", fn: (p) => p - price },
      { label: "Short Call", color: "#ef4444", fn: (p) => -Math.max(0, p - strike) },
    ] : posType === "Short Put" ? [
      { label: "Short Put", color: "#A78BFA", fn: (p) => -Math.max(0, strike - p) },
    ] : posType === "Long Call" ? [
      { label: "Long Call", color: "#4ADE80", fn: (p) => Math.max(0, p - strike) },
    ] : [],
    metrics: [
      { label: "Current Price", value: fmtFull(price), sub: f.asset || "—" },
      { label: "Expected Move", value: `±${move}%`, sub: `${fmtFull(downTarget.toFixed(0))} – ${fmtFull(upTarget.toFixed(0))}` },
      { label: "Position", value: posType, sub: `${fmtFull(strike)} strike` },
      { label: "Premium", value: fmtFull(premCollected), sub: `${cushion}% cushion`, positive: true },
      { label: "Event", value: f.event_date || "—", sub: f.recommendation || "" },
    ],
    legs: [
      { action: posType.includes("Short") || posType.includes("Covered") ? "SELL" : "BUY", type: posType, strike, label: `${fmtFull(strike)} @ ${fmtFull(premCollected)}`, color: "#FBBF24" },
    ],
    zones: [
      { from: downTarget, to: upTarget, label: "Expected Move", color: "rgba(251,191,36,0.06)" },
    ],
  };
}

// --- CALL SPREAD ---
// long_strike = the strike of the leg you are LONG (bought call)
// short_strike = the strike of the leg you are SHORT (sold call)
// For Long (bull): long_strike < short_strike; premium is negative (debit)
// For Short (bear): long_strike > short_strike; premium is positive (credit)
function computeCallSpread(f) {
  const spot = n(f.spot);
  const longK = n(f.long_strike);
  const shortK = n(f.short_strike);
  const premium = n(f.premium);
  const dir = f.direction || "Long";
  const isLong = dir === "Long";

  const lo = spot * 0.74;
  const hi = spot * 1.26;

  // Single formula works for both directions given correct strike ordering
  const pf = (S) => Math.max(0, S - longK) - Math.max(0, S - shortK) + premium;
  const curve = buildCurve(lo, hi, pf);

  const absP = Math.abs(premium);
  const breakeven = isLong ? longK + absP : shortK + absP;
  const maxGain = isLong ? Math.max(shortK - longK + premium, 0) : absP;
  const maxLoss = isLong ? absP : Math.max(longK - shortK - absP, 0);

  return {
    curve, spot, breakevens: [breakeven],
    metrics: [
      { label: "Spot Price", value: fmt(spot), sub: f.asset || "—" },
      { label: dir + " Call Spread", value: `${fmt(longK)} / ${fmt(shortK)}` },
      { label: "Expiry", value: f.expiry || "—", sub: `IV: ${f.iv || "—"} · Δ ${f.delta || "—"}` },
      { label: "Max Gain", value: fmt(maxGain), sub: isLong ? `Above ${fmt(shortK)}` : "Premium income", positive: true },
      { label: "Max Loss", value: fmt(maxLoss), sub: isLong ? `Below ${fmt(longK)}` : `Above ${fmt(longK)}`, negative: true },
      { label: "Breakeven", value: fmt(breakeven) },
    ],
    legs: isLong
      ? [
          { action: "BUY", type: "Call", strike: longK, label: `${fmt(longK)} Call`, color: "#378ADD" },
          { action: "SELL", type: "Call", strike: shortK, label: `${fmt(shortK)} Call`, color: "#ef4444" },
        ]
      : [
          { action: "SELL", type: "Call", strike: shortK, label: `${fmt(shortK)} Call`, color: "#ef4444" },
          { action: "BUY", type: "Call", strike: longK, label: `${fmt(longK)} Call`, color: "#378ADD" },
        ],
    zones: [],
  };
}

// --- PUT SPREAD ---
// long_strike = strike of bought put (higher strike for Long bear spread)
// short_strike = strike of sold put (lower strike)
function computePutSpread(f) {
  const spot = n(f.spot);
  const longK = n(f.long_strike);
  const shortK = n(f.short_strike);
  const premium = n(f.premium);
  const dir = f.direction || "Long";
  const isLong = dir === "Long";

  const lo = spot * 0.74;
  const hi = spot * 1.26;

  const pf = (S) => Math.max(0, longK - S) - Math.max(0, shortK - S) + premium;
  const curve = buildCurve(lo, hi, pf);

  const absP = Math.abs(premium);
  const breakeven = isLong ? longK - absP : shortK - absP;
  const maxGain = isLong ? Math.max(longK - shortK + premium, 0) : absP;
  const maxLoss = isLong ? absP : Math.max(longK - shortK - absP, 0);

  return {
    curve, spot, breakevens: [breakeven],
    metrics: [
      { label: "Spot Price", value: fmt(spot), sub: f.asset || "—" },
      { label: dir + " Put Spread", value: `${fmt(longK)} / ${fmt(shortK)}` },
      { label: "Expiry", value: f.expiry || "—", sub: `IV: ${f.iv || "—"} · Δ ${f.delta || "—"}` },
      { label: "Max Gain", value: fmt(maxGain), sub: isLong ? `Below ${fmt(shortK)}` : "Premium income", positive: true },
      { label: "Max Loss", value: fmt(maxLoss), sub: isLong ? `Above ${fmt(longK)}` : `Below ${fmt(shortK)}`, negative: true },
      { label: "Breakeven", value: fmt(breakeven) },
    ],
    legs: isLong
      ? [
          { action: "BUY", type: "Put", strike: longK, label: `${fmt(longK)} Put`, color: "#378ADD" },
          { action: "SELL", type: "Put", strike: shortK, label: `${fmt(shortK)} Put`, color: "#ef4444" },
        ]
      : [
          { action: "SELL", type: "Put", strike: shortK, label: `${fmt(shortK)} Put`, color: "#ef4444" },
          { action: "BUY", type: "Put", strike: longK, label: `${fmt(longK)} Put`, color: "#378ADD" },
        ],
    zones: [],
  };
}

// --- STRADDLE ---
function computeStraddle(f) {
  const spot = n(f.spot);
  const atmK = n(f.atm_strike);
  const premium = n(f.total_premium);
  const dir = f.direction || "Long";
  const isLong = dir === "Long";

  const lo = spot * 0.74;
  const hi = spot * 1.26;

  const pf = (S) => {
    const raw = Math.max(0, S - atmK) + Math.max(0, atmK - S);
    return isLong ? raw + premium : premium - raw;
  };
  const curve = buildCurve(lo, hi, pf);

  const absP = Math.abs(premium);
  const beLow = atmK - absP;
  const beHigh = atmK + absP;

  return {
    curve, spot, breakevens: [beLow, beHigh],
    metrics: [
      { label: "Spot Price", value: fmt(spot), sub: f.asset || "—" },
      { label: dir + " Straddle", value: fmt(atmK) + " ATM" },
      { label: "Expiry", value: f.expiry || "—" },
      ...(isLong
        ? [{ label: "Max Loss", value: fmt(absP), sub: "Premium paid", negative: true }]
        : [{ label: "Max Profit", value: fmt(absP), sub: "Premium received", positive: true }]
      ),
      { label: "BE Low", value: fmt(beLow), sub: isLong ? "Below = profit" : "Below = loss" },
      { label: "BE High", value: fmt(beHigh), sub: isLong ? "Above = profit" : "Above = loss" },
    ],
    legs: isLong
      ? [
          { action: "BUY", type: "Call", strike: atmK, label: `${fmt(atmK)} Call`, color: "#A78BFA" },
          { action: "BUY", type: "Put", strike: atmK, label: `${fmt(atmK)} Put`, color: "#A78BFA" },
        ]
      : [
          { action: "SELL", type: "Call", strike: atmK, label: `${fmt(atmK)} Call`, color: "#ef4444" },
          { action: "SELL", type: "Put", strike: atmK, label: `${fmt(atmK)} Put`, color: "#ef4444" },
        ],
    zones: [],
  };
}

// --- STRANGLE ---
function computeStrangle(f) {
  const spot = n(f.spot);
  const callK = n(f.call_strike);
  const putK = n(f.put_strike);
  const premium = n(f.total_premium);
  const dir = f.direction || "Long";
  const isLong = dir === "Long";

  const lo = spot * 0.74;
  const hi = spot * 1.26;

  const pf = (S) => {
    const raw = Math.max(0, S - callK) + Math.max(0, putK - S);
    return isLong ? raw + premium : premium - raw;
  };
  const curve = buildCurve(lo, hi, pf);

  const absP = Math.abs(premium);
  const beLow = putK - absP;
  const beHigh = callK + absP;

  return {
    curve, spot, breakevens: [beLow, beHigh],
    metrics: [
      { label: "Spot Price", value: fmt(spot), sub: f.asset || "—" },
      { label: dir + " Strangle", value: `${fmt(putK)} / ${fmt(callK)}` },
      { label: "Expiry", value: f.expiry || "—" },
      ...(isLong
        ? [{ label: "Max Loss", value: fmt(absP), sub: "Premium paid", negative: true }]
        : [{ label: "Max Profit", value: fmt(absP), sub: "Premium received", positive: true }]
      ),
      { label: "BE Low", value: fmt(beLow), sub: isLong ? "Below = profit" : "Below = loss" },
      { label: "BE High", value: fmt(beHigh), sub: isLong ? "Above = profit" : "Above = loss" },
    ],
    legs: isLong
      ? [
          { action: "BUY", type: "Call", strike: callK, label: `${fmt(callK)} Call`, color: "#34D399" },
          { action: "BUY", type: "Put", strike: putK, label: `${fmt(putK)} Put`, color: "#34D399" },
        ]
      : [
          { action: "SELL", type: "Call", strike: callK, label: `${fmt(callK)} Call`, color: "#ef4444" },
          { action: "SELL", type: "Put", strike: putK, label: `${fmt(putK)} Put`, color: "#ef4444" },
        ],
    zones: [],
  };
}

// --- BINARY OPTION ---
// Each contract pays $1 if the event occurs (price ≥ strike for call, price ≤ strike for put),
// $0 otherwise. Premium is entered as a percentage (e.g. 40 = 40%) and represents both the
// cost per contract ($0.40) and the implied probability of the event.
// Payout multiple = 1 / premium (e.g. 40% → 2.5×).
function computeBinaryOption(f) {
  const spot      = n(f.spot);
  const strike    = n(f.strike);
  const type      = (f.type || "Call");
  const direction = (f.direction || "Long");
  const isCall    = type === "Call";
  const isLong    = direction === "Long";
  const quantity  = Math.max(1, n(f.quantity) || 1);
  const premiumPct = Math.max(0.01, Math.min(99.99, n(f.premium))); // clamp to (0,100) %
  const costPerContract = premiumPct / 100;            // dollars per contract
  const totalCost  = costPerContract * quantity;       // dollars paid (long) / received (short)
  const totalMaxPayout = 1 * quantity;                 // $1 per contract × N

  const payoutMultiple = costPerContract > 0 ? (1 / costPerContract) : 0;

  // Per-position P&L if the event occurs vs. doesn't occur
  // - Long: pay totalCost up front; on event receive totalMaxPayout → net = payout - cost
  // - Short: collect totalCost up front; on event pay totalMaxPayout → net = cost - payout
  const pnlEvent    = isLong ? (totalMaxPayout - totalCost) : (totalCost - totalMaxPayout);
  const pnlNoEvent  = isLong ? (-totalCost)                 : (totalCost);

  const eventHappens = (price) => isCall ? price >= strike : price <= strike;
  const pnlAtPrice = (price) => eventHappens(price) ? pnlEvent : pnlNoEvent;

  const maxProfit = Math.max(pnlEvent, pnlNoEvent);
  const maxLoss   = Math.min(pnlEvent, pnlNoEvent);

  // Chart bounds — show strike with ±30% breathing room, ensure spot is visible
  const center = strike || spot || 1;
  const baseHalf = center * 0.3;
  const minP = Math.max(Math.min(center - baseHalf, (spot || center) * 0.85), 0.01);
  const maxP = Math.max(center + baseHalf, (spot || center) * 1.15);

  // Build a piecewise step curve — explicitly include the strike from both sides so the
  // vertical jump renders sharply at the threshold instead of being smoothed by interpolation.
  const curve = (() => {
    const pts = [];
    const STEPS = 200;
    for (let i = 0; i <= STEPS; i++) {
      const price = minP + (maxP - minP) * (i / STEPS);
      pts.push({ price, pnl: pnlAtPrice(price) });
    }
    // Insert exact left-of-strike and right-of-strike points to render the step cleanly
    const eps = (maxP - minP) * 1e-5;
    pts.push({ price: strike - eps, pnl: pnlAtPrice(strike - eps) });
    pts.push({ price: strike + eps, pnl: pnlAtPrice(strike + eps) });
    pts.sort((a, b) => a.price - b.price);
    return pts;
  })();

  // Display formatters
  const fmtExact = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  // Per-contract cost is sub-dollar (e.g. $0.40 for 40% premium) — keep 2-4 decimals so we don't show "$0".
  const fmtPerContract = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  const fmtPct = (v) => `${(Math.round(v * 100) / 100)}%`;

  // Action verbs for legs
  const action = isLong ? "BUY" : "SELL";
  const legColor = isLong ? "#8B5CF6" : "#ef4444";

  // Profit zone — side of strike where the event pays
  const profitZone = pnlEvent > pnlNoEvent
    ? (isCall ? { from: strike, to: maxP } : { from: minP, to: strike })
    : (isCall ? { from: minP, to: strike } : { from: strike, to: maxP });

  return {
    curve,
    spot,
    breakevens: [], // Binary options don't have a continuous breakeven — the strike IS the trigger.
    tradeType: "binary_option",
    currentNotional: totalCost,
    positionSize: quantity,
    pnlAtPrice,
    maxProfit,
    maxLoss,
    maxProfitBounded: true,
    maxLossBounded: true,
    returnDenominator: { value: Math.abs(totalCost) || 1, label: "Cost at risk" },
    spotQuantity: 0, // Binary options don't track spot exposure — no Long P&L reference line.
    legPayoffs: [
      { label: `${direction} Binary ${type}`, color: legColor, fn: (p) => pnlAtPrice(p) },
    ],
    metrics: [
      { label: "Spot Price",    value: fmtFull(spot),                                              sub: f.asset || "—" },
      { label: `${direction} Binary ${type}`, value: fmtFull(strike),                              sub: `Strike · ${quantity.toLocaleString()} contracts` },
      { label: "Premium",       value: fmtPct(premiumPct),                                         sub: `${fmtPerContract(costPerContract)} per contract` },
      { label: "Payout Multiple", value: `${payoutMultiple.toFixed(2)}×`,                          sub: "1 ÷ premium" },
      { label: "Total Cost",    value: fmtExact(Math.abs(totalCost)),                              sub: isLong ? "Paid upfront" : "Received upfront", positive: !isLong, negative: isLong },
      { label: "Max Profit",    value: fmtExact(maxProfit),                                        sub: pnlEvent > pnlNoEvent ? "If event occurs" : "If event does not occur", positive: true },
      { label: "Max Loss",      value: fmtExact(Math.abs(maxLoss)),                                sub: pnlEvent > pnlNoEvent ? "If event does not occur" : "If event occurs", negative: true },
      { label: "Expiry",        value: f.expiry || "—",                                            sub: `Trigger at ${fmtFull(strike)}` },
    ],
    legs: [
      { action, type: `Binary ${type}`, strike, label: `${quantity.toLocaleString()} × ${fmtFull(strike)} Binary ${type} @ ${fmtPct(premiumPct)}`, color: legColor },
    ],
    zones: [
      { from: profitZone.from, to: profitZone.to, label: "Profit Zone", color: "rgba(139,92,246,0.10)" },
    ],
  };
}
